import { api, apiTags } from '~shared/api/origin';

import type {
	CancelSubscriptionApiRequest,
	GetAssignedPromocodesApiResponse,
	GetSubscriptionBenefitsApiResponse,
	GetSubscriptionsApiResponse,
} from './types';

export const {
	util,
	useGetSubscriptionsQuery,
	useGetSubscriptionBenefitsQuery,
	useCancelSubscriptionMutation,
	useRestoreSubscriptionMutation,
	useGetAssignedPromocodesQuery,
	useAssignSubscriptionPromocodeMutation,
} = api.injectEndpoints({
	endpoints: (build) => ({
		getSubscriptions: build.query<GetSubscriptionsApiResponse, { id: string }>({
			query: ({ id }) => ({
				url: `/benefits/${id}/subscriptions/actual`,
			}),
			providesTags: [apiTags.subscriptions],
		}),

		getSubscriptionBenefits: build.query<
			GetSubscriptionBenefitsApiResponse,
			{ id: string }
		>({
			query: ({ id }) => ({
				url: `/ai/chats/${id}/subscription-benefits/premium`,
			}),
		}),

		cancelSubscription: build.mutation<
			undefined,
			{ id: string; body: CancelSubscriptionApiRequest }
		>({
			query: ({ id, body }) => ({
				url: `/benefits/${id}/subscriptions/cancel`,
				method: 'POST',
				body,
			}),
			async onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;
					dispatch(
						util.updateQueryData('getSubscriptions', { id }, (draft) => {
							const item = draft.subscriptions.find(
								(item) => item.sku === body.sku
							);
							if (!item) return;
							item.cancelDate = item.endDate;
						})
					);
				} catch {}
			},
		}),

		restoreSubscription: build.mutation<undefined, { userId: string }>({
			query: ({ userId }) => ({
				url: `/benefits/${userId}/subscriptions/restore`,
				method: 'POST',
			}),
			invalidatesTags: [apiTags.subscriptions],
		}),

		getAssignedPromocodes: build.query<
			GetAssignedPromocodesApiResponse,
			{ userId: string }
		>({
			query: ({ userId }) => ({
				url: `ai/benefits/${userId}/promocodes/assigned`,
			}),
			providesTags: [apiTags.assignedPromocodes],
		}),

		assignSubscriptionPromocode: build.mutation<
			undefined,
			{ userId: string; promocode: string }
		>({
			query: ({ userId, promocode }) => ({
				url: `ai/benefits/${userId}/promocodes/assigned`,
				method: 'POST',
				body: {
					promoCode: promocode,
				},
			}),
			invalidatesTags: [apiTags.assignedPromocodes],
		}),
	}),
});
